import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fetchClass, fetchInstanceRsvps, fetchClassFollowers } from "../lib/schools";
import {
  buildClassShareText,
  CLASS_DASHBOARD_TABS,
  formatClassScheduleLabel,
  formatSessionDatePill,
  normalizeClassDashboardTab,
} from "../lib/classDashboard";
import { classPublicHref, classSessionDateKey } from "../lib/classLinks";
import { copyWhatsAppSummary, openWhatsAppShare } from "../lib/share";
import { ReadableNavButton } from "../components/Sticker";
import { TabButton } from "../components/ui";
import DashboardLoadingSkeleton from "../components/dashboard/DashboardLoadingSkeleton";
import ClassDashboardHeader from "../components/classDashboard/ClassDashboardHeader";
import SessionDateSelector from "../components/classDashboard/SessionDateSelector";
import RosterAttendanceTab from "../components/classDashboard/RosterAttendanceTab";
import BroadcastMessageTab from "../components/classDashboard/BroadcastMessageTab";
import ClassEditWeekTab from "../components/classDashboard/ClassEditWeekTab";
import ClassShareFooter from "../components/classDashboard/ClassShareFooter";

function instanceIdOf(inst) {
  return inst?.id || inst?._id || null;
}

function pickDefaultInstance(instances, preferredId) {
  if (!instances?.length) return null;
  if (preferredId) {
    const match = instances.find((i) => instanceIdOf(i) === preferredId);
    if (match) return match;
  }
  const upcoming = instances.find((i) => {
    if (i.status === "CANCELLED") return false;
    const t = new Date(i.effective_datetime || i.datetime).getTime();
    return !Number.isNaN(t) && t >= Date.now() - 3 * 60 * 60 * 1000;
  });
  return upcoming || instances[0];
}

export default function ClassDashboardPage() {
  const { classId } = useParams();
  const [params, setParams] = useSearchParams();
  const { account } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rsvps, setRsvps] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [followers, setFollowers] = useState([]);
  const [followersLoading, setFollowersLoading] = useState(false);
  const [error, setError] = useState(null);

  const tab = normalizeClassDashboardTab(params.get("tab"));
  const instanceParam = params.get("instance");

  const loadClass = useCallback(async () => {
    const result = await fetchClass(classId);
    setData(result);
    return result;
  }, [classId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await loadClass();
        if (cancelled) return;
        if (!result?.is_admin) {
          setError("admin");
        }
      } catch (e) {
        if (!cancelled) {
          setError("load");
          toast.error(formatApiError(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadClass]);

  const instances = useMemo(() => {
    const list = data?.instances || [];
    return [...list].sort((a, b) => {
      const ta = new Date(a.effective_datetime || a.datetime).getTime();
      const tb = new Date(b.effective_datetime || b.datetime).getTime();
      return ta - tb;
    });
  }, [data?.instances]);

  const selectedInstance = useMemo(
    () => pickDefaultInstance(instances, instanceParam),
    [instances, instanceParam],
  );
  const selectedInstanceId = instanceIdOf(selectedInstance);

  useEffect(() => {
    if (!selectedInstanceId) return;
    if (instanceParam === selectedInstanceId) return;
    const next = new URLSearchParams(params);
    next.set("instance", selectedInstanceId);
    setParams(next, { replace: true });
    // Intentionally omit `params` — only sync when the resolved instance changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstanceId, instanceParam, setParams]);

  const loadRoster = useCallback(async (instanceId) => {
    if (!instanceId) {
      setRsvps([]);
      return;
    }
    setRosterLoading(true);
    try {
      const result = await fetchInstanceRsvps(instanceId);
      setRsvps(result?.rsvps || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setRsvps([]);
    } finally {
      setRosterLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!data?.is_admin || !selectedInstanceId) return;
    loadRoster(selectedInstanceId);
  }, [data?.is_admin, selectedInstanceId, loadRoster]);

  const loadFollowers = useCallback(async (instanceId) => {
    setFollowersLoading(true);
    try {
      const result = await fetchClassFollowers(classId, { instanceId });
      setFollowers(result?.followers || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setFollowers([]);
    } finally {
      setFollowersLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    if (tab !== "broadcast" || !data?.is_admin) return;
    loadFollowers(selectedInstanceId);
  }, [tab, data?.is_admin, selectedInstanceId, loadFollowers]);

  function setTab(nextTab) {
    const next = new URLSearchParams(params);
    next.set("tab", nextTab);
    setParams(next, { replace: true });
  }

  function selectInstance(id) {
    const next = new URLSearchParams(params);
    next.set("instance", id);
    setParams(next, { replace: true });
  }

  function handleDashboardTabKeyDown(e) {
    const navKeys = ["ArrowLeft", "ArrowRight", "Home", "End"];
    if (!navKeys.includes(e.key)) return;
    const tabs = [...e.currentTarget.querySelectorAll('[role="tab"]')];
    if (tabs.length === 0) return;
    const currentIndex = tabs.indexOf(document.activeElement);
    if (currentIndex === -1 && e.key !== "Home" && e.key !== "End") return;
    e.preventDefault();
    let nextIndex = currentIndex;
    if (e.key === "ArrowRight") nextIndex = (currentIndex + 1) % tabs.length;
    else if (e.key === "ArrowLeft") nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") nextIndex = 0;
    else if (e.key === "End") nextIndex = tabs.length - 1;
    const nextTabEl = tabs[nextIndex];
    const tabKey = nextTabEl?.dataset?.tabKey;
    if (!tabKey) return;
    nextTabEl.focus();
    setTab(tabKey);
  }

  function handleRosterChange(rsvp, opts = {}) {
    setRsvps((prev) => {
      if (opts.append) {
        if (prev.some((r) => r.id === rsvp.id)) {
          return prev.map((r) => (r.id === rsvp.id ? { ...r, ...rsvp } : r));
        }
        return [...prev, rsvp];
      }
      return prev.map((r) => (r.id === rsvp.id ? { ...r, ...rsvp } : r));
    });
  }

  async function handleCancelled() {
    await loadClass();
  }

  if (loading) return <DashboardLoadingSkeleton />;

  if (error === "admin" || (data && !data.is_admin)) {
    const publicClassPath = classPublicHref({
      schoolSlug: data?.school?.slug,
      classSlug: data?.class?.slug,
      public_path: data?.class?.public_path,
    }) || (data?.school?.slug ? `/school/${data.school.slug}` : "/");
    return (
      <div className="min-h-screen bg-canvas font-body text-ink">
        <div className="max-w-page mx-auto px-4 py-10 text-center space-y-4">
          <p className="font-ui text-[15px] text-ink/80">
            You need to be a school admin to open the class dashboard.
          </p>
          <ReadableNavButton as={Link} to={publicClassPath} data-testid="class-dash-denied-public">
            View public class page
          </ReadableNavButton>
          {!account ? (
            <ReadableNavButton as={Link} to="/login" variant="primary" data-testid="class-dash-denied-login">
              Sign in
            </ReadableNavButton>
          ) : null}
        </div>
      </div>
    );
  }

  if (!data?.class) {
    return (
      <div className="max-w-page mx-auto px-4 py-10 text-center text-ink/70">
        Class not found.
      </div>
    );
  }

  const cls = data.class;
  const school = data.school;
  const venue = data.venue;
  const schoolSlug = school?.slug || cls.school_id;
  const scheduleLabel = formatClassScheduleLabel(cls.recurrence);
  const locationLabel = [venue?.name, venue?.city || school?.city].filter(Boolean).join(", ");
  const sessionIso = selectedInstance?.effective_datetime || selectedInstance?.datetime;
  const sessionLabel = sessionIso ? formatSessionDatePill(sessionIso) : null;
  const cancelled = selectedInstance?.status === "CANCELLED";
  const publicPath = classPublicHref(
    {
      schoolSlug: school?.slug,
      classSlug: cls.slug,
      public_path: cls.public_path,
    },
    { date: classSessionDateKey(sessionIso) },
  );
  const editPath = schoolSlug ? `/admin/${schoolSlug}?tab=classes` : null;
  const publicUrl = typeof window !== "undefined"
    ? `${window.location.origin}${publicPath}`
    : publicPath;
  const shareText = buildClassShareText({
    className: cls.name,
    scheduleLabel,
    locationLabel,
    link: publicUrl,
  });

  return (
    <div className="min-h-screen bg-canvas font-body text-ink pb-10" data-testid="class-dashboard-page">
      <ClassDashboardHeader
        schoolSlug={schoolSlug}
        className={cls.name}
        scheduleLabel={scheduleLabel}
        locationLabel={locationLabel}
        publicPath={publicPath}
        editPath={editPath}
      />

      <SessionDateSelector
        instances={instances}
        selectedId={selectedInstanceId}
        onSelect={selectInstance}
      />

      <div className="max-w-page mx-auto px-4 mt-6">
        <div
          className="inline-flex gap-2 flex-wrap"
          role="tablist"
          aria-label="Class dashboard sections"
          data-testid="class-dash-tabs"
          onKeyDown={handleDashboardTabKeyDown}
        >
          {CLASS_DASHBOARD_TABS.map((t) => (
            <TabButton
              key={t.key}
              tabKey={t.key}
              active={tab === t.key}
              onClick={() => setTab(t.key)}
              testid={`class-dash-tab-${t.key}`}
              panelId={t.panelId}
            >
              {t.label}
            </TabButton>
          ))}
        </div>
      </div>

      {tab === "roster" && (
        <div role="tabpanel" id="class-dash-panel-roster" aria-labelledby="class-dash-tab-roster">
          <RosterAttendanceTab
            key={selectedInstanceId || "none"}
            instanceId={selectedInstanceId}
            className={cls.name}
            schoolName={school?.name || ""}
            sessionLabel={sessionLabel}
            rsvps={rsvps}
            loading={rosterLoading}
            cancelled={cancelled}
            onRosterChange={handleRosterChange}
          />
        </div>
      )}

      {tab === "broadcast" && (
        <div role="tabpanel" id="class-dash-panel-broadcast" aria-labelledby="class-dash-tab-broadcast">
          <BroadcastMessageTab
            sessionLabel={sessionLabel}
            expectedCount={rsvps.length}
            followers={followers}
            followersLoading={followersLoading}
          />
        </div>
      )}

      {tab === "edit" && (
        <div role="tabpanel" id="class-dash-panel-edit" aria-labelledby="class-dash-tab-edit">
          <ClassEditWeekTab
            instanceId={selectedInstanceId}
            sessionLabel={sessionLabel}
            cancelled={cancelled}
            onCancelled={handleCancelled}
          />
        </div>
      )}

      <ClassShareFooter
        onWhatsApp={() => openWhatsAppShare(shareText)}
        onCopyLink={() => {
          navigator.clipboard.writeText(publicUrl);
          toast.success("Class link copied");
        }}
        onCopyShareText={async () => {
          await copyWhatsAppSummary(shareText);
          toast.success("Share text copied");
        }}
      />
    </div>
  );
}
